import React from "react";
import {
  Alert,
  Box,
  Button,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import { useParams } from "react-router-dom";
import { erpApi } from "../../api/erp";
import { projectsApi } from "../../api/projects";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import { ErpEmpty, ErpLoading, ErpMetricCard, ErpPanel, MovementStatusChip } from "../../components/dashboard/erp/ErpUi";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import { ERP_BRAND, MOVEMENT_KIND_LABELS, formatDateTime, formatMoney, toDateInput } from "../../utils/erp";

function startOfMonth() {
  const date = new Date();
  return toDateInput(new Date(date.getFullYear(), date.getMonth(), 1));
}

export default function ProjectStatsPage() {
  const { projectId } = useParams();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [overview, setOverview] = React.useState(null);
  const [range, setRange] = React.useState({ preset: "month", dateFrom: startOfMonth(), dateTo: toDateInput() });
  const [loadingProject, setLoadingProject] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId) || activeProject,
    [projects, projectId, activeProject],
  );
  const resolvedProject = project || contextProject;
  const enabled = isProjectModuleEnabled(resolvedProject, "stats");
  const systemEnabled = isProjectModuleEnabled(resolvedProject, "system");

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    let mounted = true;
    setLoadingProject(true);
    projectsApi.get(projectId)
      .then((data) => {
        if (!mounted) return;
        setProject(data.project || null);
        if (data.project) setActiveProject(data.project);
      })
      .catch((err) => {
        if (mounted) setError(err.message || "No se pudo cargar el proyecto");
      })
      .finally(() => {
        if (mounted) setLoadingProject(false);
      });
    return () => { mounted = false; };
  }, [projectId, setActiveProject]);

  const loadStats = React.useCallback(async () => {
    if (!enabled || !systemEnabled) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.overview(projectId, { dateFrom: range.dateFrom, dateTo: range.dateTo });
      setOverview(data.overview || null);
    } catch (err) {
      setError(err.message || "No se pudieron cargar las estadísticas");
    } finally {
      setLoading(false);
    }
  }, [enabled, systemEnabled, projectId, range.dateFrom, range.dateTo]);

  React.useEffect(() => { if (!loadingProject) loadStats(); }, [loadingProject, loadStats]);

  const applyPreset = (preset) => {
    const now = new Date();
    if (preset === "today") {
      const today = toDateInput(now);
      setRange({ preset, dateFrom: today, dateTo: today });
    } else if (preset === "week") {
      const from = new Date(now);
      from.setDate(now.getDate() - 6);
      setRange({ preset, dateFrom: toDateInput(from), dateTo: toDateInput(now) });
    } else if (preset === "month") {
      setRange({ preset, dateFrom: startOfMonth(), dateTo: toDateInput(now) });
    } else {
      setRange((current) => ({ ...current, preset }));
    }
  };

  if (loadingProject) return <Alert severity="info">Cargando estadísticas...</Alert>;
  if (!enabled) {
    return <ModuleDisabledState projectId={projectId} title="Estadísticas" description="Este módulo reúne indicadores operativos y comerciales del proyecto." />;
  }
  if (!systemEnabled) {
    return (
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Estadísticas</Typography>
          <Typography sx={{ color: ERP_BRAND.muted, mt: 0.5 }}>{resolvedProject?.name || projectId}</Typography>
        </Box>
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Las estadísticas ERP estarán disponibles cuando el módulo Sistema esté activo. Las métricas de visitas, productos y pedidos se conectarán en su etapa correspondiente.
        </Alert>
      </Stack>
    );
  }

  const sales = overview?.sales || {};
  const purchases = overview?.purchases || {};
  const ledger = overview?.ledger || {};
  const movements = overview?.movements || {};
  const recent = overview?.recentMovements || [];
  const movementRows = Object.entries(movements).map(([kind, values]) => ({ kind, ...values })).sort((a, b) => Number(b.total || 0) - Number(a.total || 0));
  const maxTotal = Math.max(1, ...movementRows.map((row) => Number(row.total || 0)));
  const net = Number(sales.total || 0) - Number(purchases.total || 0);

  return (
    <Stack spacing={2.6}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.2} flexWrap="wrap" useFlexGap>
            <Typography variant="h3" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Estadísticas</Typography>
            <Chip icon={<QueryStatsRoundedIcon />} label="Datos ERP reales" color="success" size="small" sx={{ fontWeight: 850 }} />
          </Stack>
          <Typography sx={{ color: ERP_BRAND.muted, mt: 0.5 }}>{resolvedProject?.name || projectId} · rendimiento del período</Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={loadStats} sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 850 }}>Actualizar</Button>
      </Stack>

      <ErpPanel>
        <CardContent sx={{ p: 2.2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.3} alignItems={{ xs: "stretch", md: "center" }}>
            <TextField select size="small" label="Período" value={range.preset} onChange={(event) => applyPreset(event.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="today">Hoy</MenuItem>
              <MenuItem value="week">Últimos 7 días</MenuItem>
              <MenuItem value="month">Mes actual</MenuItem>
              <MenuItem value="custom">Personalizado</MenuItem>
            </TextField>
            <TextField size="small" type="date" label="Desde" value={range.dateFrom} onChange={(event) => setRange((current) => ({ ...current, preset: "custom", dateFrom: event.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="Hasta" value={range.dateTo} onChange={(event) => setRange((current) => ({ ...current, preset: "custom", dateTo: event.target.value }))} InputLabelProps={{ shrink: true }} />
            <Typography variant="body2" sx={{ color: ERP_BRAND.muted, ml: { md: "auto" } }}>Los importes incluyen movimientos confirmados dentro del período.</Typography>
          </Stack>
        </CardContent>
      </ErpPanel>

      {loading ? <ErpLoading label="Calculando indicadores..." /> : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} xl={3}><ErpMetricCard icon={<TrendingUpRoundedIcon />} label="VENTAS" value={formatMoney(sales.total)} detail={`${sales.count || 0} operaciones · ${formatMoney(sales.pending)} pendiente`} tone="green" /></Grid>
            <Grid item xs={12} sm={6} xl={3}><ErpMetricCard icon={<ShoppingCartCheckoutRoundedIcon />} label="COMPRAS" value={formatMoney(purchases.total)} detail={`${purchases.count || 0} operaciones · ${formatMoney(purchases.pending)} pendiente`} tone="orange" /></Grid>
            <Grid item xs={12} sm={6} xl={3}><ErpMetricCard icon={<PointOfSaleRoundedIcon />} label="RESULTADO OPERATIVO" value={formatMoney(net)} detail="Ventas menos compras del período" tone={net >= 0 ? "blue" : "red"} /></Grid>
            <Grid item xs={12} sm={6} xl={3}><ErpMetricCard icon={<AccountBalanceRoundedIcon />} label="ASIENTOS" value={`${ledger.entries || 0} líneas`} detail={`${formatMoney(ledger.debits)} debe · ${formatMoney(ledger.credits)} haber`} tone="dark" /></Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={7}>
              <ErpPanel>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Composición de movimientos</Typography>
                  <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.4 }}>Volumen e importe por tipo de operación.</Typography>
                  {!movementRows.length ? <ErpEmpty title="Sin actividad en el período" description="Probá ampliar las fechas o registrá movimientos confirmados." /> : (
                    <Stack spacing={2} sx={{ mt: 2.5 }}>
                      {movementRows.map((row) => (
                        <Box key={row.kind}>
                          <Stack direction="row" justifyContent="space-between" spacing={2}>
                            <Box>
                              <Typography sx={{ fontWeight: 900 }}>{MOVEMENT_KIND_LABELS[row.kind] || row.kind}</Typography>
                              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{row.count || 0} operaciones · {formatMoney(row.pending)} pendiente</Typography>
                            </Box>
                            <Typography sx={{ fontWeight: 950 }}>{formatMoney(row.total)}</Typography>
                          </Stack>
                          <LinearProgress variant="determinate" value={Math.max(2, (Number(row.total || 0) / maxTotal) * 100)} sx={{ mt: 1, height: 8, borderRadius: 999 }} />
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </ErpPanel>
            </Grid>
            <Grid item xs={12} lg={5}>
              <ErpPanel sx={{ height: "100%" }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Actividad reciente</Typography>
                  <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.4 }}>Últimos movimientos del sistema.</Typography>
                  {!recent.length ? <ErpEmpty title="Sin movimientos recientes" description="No hay actividad confirmada o anulada para mostrar." /> : (
                    <Stack spacing={0} sx={{ mt: 1.5 }}>
                      {recent.map((movement, index) => (
                        <Box key={movement.id || movement._id} sx={{ py: 1.5, borderTop: index ? `1px solid ${ERP_BRAND.border}` : "none" }}>
                          <Stack direction="row" justifyContent="space-between" spacing={2}>
                            <Box sx={{ minWidth: 0 }}>
                              <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography sx={{ fontWeight: 900 }}>{movement.movementNumber}</Typography>
                                <MovementStatusChip status={movement.status} />
                              </Stack>
                              <Typography noWrap variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.45 }}>{movement.description || MOVEMENT_KIND_LABELS[movement.kind]}</Typography>
                              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{formatDateTime(movement.occurredAt)}</Typography>
                            </Box>
                            <Typography sx={{ fontWeight: 950, flexShrink: 0 }}>{formatMoney(movement.total, movement.currency)}</Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </ErpPanel>
            </Grid>
          </Grid>
        </>
      )}
    </Stack>
  );
}
